import { useSelector } from 'react-redux';

import type { BonusCampaign } from '../../../api/fetchers/bonusPromotions/types/bonus';
import { BonusStatus } from '../../../api/fetchers/bonusPromotions/types/constants';
import { computeFreeChipBalance } from '../../../utils/chips';

import type { BonusSliceState } from './types';

export const selectFreeChipBalance = (state: BonusSliceState): number =>
  computeFreeChipBalance(state.bonusPromotions.activeBonusCampaign?.bonus);

export const selectIsBonusCampaignActive = (state: BonusSliceState): boolean =>
  state.bonusPromotions.activeBonusCampaign?.bonus?.status === BonusStatus.Active;

export const selectClaimedBonus = (state: BonusSliceState): BonusCampaign | null => {
  return selectIsBonusCampaignActive(state) ? state.bonusPromotions.eligibleBonusCampaign : null;
};

export const useBonusPromotionsSlice = {
  getBaseUrl: (): string | undefined => {
    return useSelector((state: BonusSliceState) => state.bonusPromotions.envBonusPromo);
  },
  getClaimedBonus: (): BonusCampaign | null => {
    return useSelector(selectClaimedBonus);
  },
  getIsBonusCampaignActive: (): boolean => {
    return useSelector(selectIsBonusCampaignActive);
  },
  getUnClaimedBonus: (): BonusCampaign | null => {
    return useSelector((state: BonusSliceState) => {
      const isEligbleForClaim = state.bonusPromotions.eligibleBonusCampaign
        ? state.bonusPromotions.eligibleBonusCampaign.bonus.status === BonusStatus.Pending
        : false;
      return isEligbleForClaim ? state.bonusPromotions.eligibleBonusCampaign : null;
    });
  },
  getCancelledBonus: (): BonusCampaign[] => {
    return useSelector((state: BonusSliceState) => state.bonusPromotions.cancelledBonusCampaigns);
  },
  getWageredBonus: (): BonusCampaign[] => {
    return useSelector((state: BonusSliceState) => state.bonusPromotions.wageredBonusCampaigns);
  },
  getExpiredBonus: (): BonusCampaign[] => {
    return useSelector((state: BonusSliceState) => state.bonusPromotions.expiredBonusCampaigns);
  },
  getShowBonusConsent: (): boolean => {
    return useSelector(
      (state: BonusSliceState) =>
        state.bonusPromotions.eligibleBonusCampaign?.bonus.status === BonusStatus.Pending &&
        !state.bonusPromotions.isAcknowledged
    );
  },
  getShowBonusNotification: (): boolean => {
    return useSelector(
      (state: BonusSliceState) =>
        state.bonusPromotions.wageredBonusCampaigns.length > 0 ||
        state.bonusPromotions.cancelledBonusCampaigns.length > 0 ||
        state.bonusPromotions.expiredBonusCampaigns.length > 0
    );
  },
  getIsBonusPromotionsFetching: (): boolean => {
    return useSelector((state: BonusSliceState) => state.bonusPromotions.isBonusPromotionsFetching);
  },
  getIsBonusPromotionsInitialised: (): boolean => {
    return useSelector((state: BonusSliceState) => state.bonusPromotions.isBonusPromotionsInitialised);
  },
  getIsAcknowledged: (): boolean => {
    return useSelector((state: BonusSliceState) => state.bonusPromotions.isAcknowledged);
  },
  getEarBonus: (): BonusCampaign | null => {
    return useSelector((state: BonusSliceState) => {
      return (
        state.bonusPromotions.expiredBonusCampaigns[0] ??
        state.bonusPromotions.wageredBonusCampaigns[0] ??
        state.bonusPromotions.cancelledBonusCampaigns[0] ??
        state.bonusPromotions.eligibleBonusCampaign
      );
    });
  },
  getIsBonusHistoryLoading: (): boolean => {
    return useSelector((state: BonusSliceState) => state.bonusPromotions.isBonusHistoryLoading);
  },
  getSilentAckBonusIds: (): Array<number> => {
    return useSelector((state: BonusSliceState) => state.bonusPromotions.silentAckBonusIds);
  },
  getForceBonusRefresh: (): boolean => {
    return useSelector((state: BonusSliceState) => state.bonusPromotions.forceBonusRefresh);
  },

  getFreeChipValue: (): number => {
    return useSelector((state: BonusSliceState) => state.bonusPromotions.activeBonusCampaign?.bonus.chipValue ?? 0);
  },
  getMaxFreeBetLimit: (): number => {
    return useSelector((state: BonusSliceState) => {
      const bonus = state.bonusPromotions.activeBonusCampaign?.bonus;
      if (!bonus) {
        return Infinity;
      }
      return bonus.chipValue * bonus.maxBetLimit;
    });
  },
  getFreeChipBalance: (): number => {
    return useSelector(selectFreeChipBalance);
  },
  getRefetchCampaign: (): boolean => {
    return useSelector((state: BonusSliceState) => state.bonusPromotions.refetchCampaign ?? false);
  },
  getIsBonusUpdateRequired: (): boolean => {
    return useSelector((state: BonusSliceState) => state.bonusPromotions.isBonusUpdateRequired ?? false);
  },
  getIsGameRoundOngoing: (): boolean => {
    return useSelector((state: BonusSliceState) => state.bonusPromotions.isGameRoundOngoing ?? false);
  },
};
